import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Linking, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import type { ItineraryItem } from '@/services/itinerary';
import { updateItineraryItem } from '@/services/itinerary';
import { updateBooking, type Booking, type FlightNotificationPreferences } from '@/services/bookings';
import { FlightStatusCard, withTimeOnDate } from '@/components/bookings/FlightStatusCard';
import {
  refreshFlightStatus,
  fetchCheckInLink,
  getFallbackCheckInUrl,
  updateNotificationPreferences as updateFlightNotificationPreferences,
} from '@/services/flightStatus';
import {
  normalizeAttachments,
  resolveItineraryAttachmentLocalUri,
  type ItineraryAttachment,
} from '@/services/itineraryAttachments';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { buildGoogleMapsSearchUrl, formatItineraryLocationDisplay } from '@/utils/externalMapsQuery';
import { mergeItineraryTimeOntoDay } from '@/utils/mergeItineraryTimeOntoDay';
import { ItemNotesSection } from './ItemNotesSection';
import { useToast } from '@/components/ui/ToastProvider';
import { GettingAroundRow } from '@/components/planning/GettingAroundRow';
import type { Trip } from '@/services/trips';

/** Calendar hint even when startTime is flexible or missing (date-only Planner row). */
function itineraryCalendarHint(day: Date | undefined, time?: string): Date | undefined {
  const withTime = withTimeOnDate(day, time);
  if (withTime) return withTime;
  if (!day) return undefined;
  const d = day instanceof Date ? new Date(day) : new Date(day);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setHours(0, 0, 0, 0);
  return d;
}

export type ItineraryItemDetailsContentProps = {
  item: ItineraryItem;
  booking?: Booking | null;
  tripId?: string | null;
  trip?: Trip | null;
  isTripActive?: boolean;
  onOpenMobility?: (itemId: string) => void;
  onEditItem?: (item: ItineraryItem) => void;
  onDeleteItem?: (item: ItineraryItem) => void;
  onViewDocument?: (
    url: string,
    meta?: { offlinePath?: string | null; fileName?: string; contentType?: string | null },
  ) => void;
  canEdit?: boolean;
  onMarkDoneSuccess?: () => void;
};

const categoryLabel = (c: ItineraryItem['category']) => {
  switch (c) {
    case 'flight':
      return 'Flight';
    case 'hotel':
      return 'Hotel';
    case 'activity':
      return 'Activity';
    case 'restaurant':
      return 'Food';
    case 'transportation':
      return 'Transit';
    case 'train':
      return 'Train';
    case 'ferry':
      return 'Ferry';
    case 'car':
      return 'Car';
    case 'shopping':
      return 'Shopping';
    default:
      return 'Event';
  }
};

const formatTime = (time?: string): string | null => {
  if (!time || time === 'flexible') return time === 'flexible' ? 'Flexible' : null;
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return null;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes || '00'} ${ampm}`;
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

export function ItineraryItemDetailsContent({
  item,
  booking,
  tripId,
  trip,
  isTripActive = false,
  onOpenMobility,
  onEditItem,
  onDeleteItem,
  onViewDocument,
  canEdit = false,
  onMarkDoneSuccess,
}: ItineraryItemDetailsContentProps) {
  const theme = useTheme();
  const { showSuccess, showError } = useToast();
  const [showAttachments, setShowAttachments] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [updatingPrefs, setUpdatingPrefs] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);
  const [markedDoneLocally, setMarkedDoneLocally] = useState(false);

  const isDone =
    markedDoneLocally || item.status === 'completed' || item.status === 'cancelled';
  const canMarkDone = Boolean(canEdit && tripId && !isDone && !markingDone);

  useEffect(() => {
    setMarkedDoneLocally(false);
  }, [item.id, item.status]);

  const handleRefreshFlightStatus = useCallback(async () => {
    if (!tripId || !booking?.id) return;
    setRefreshingStatus(true);
    try {
      const normalizeCode = (value?: string | null) => {
        const normalized = value?.trim().toUpperCase();
        return normalized && normalized.length > 0 ? normalized : null;
      };
      const itineraryAirlineCode = normalizeCode(item.airlineCode);
      const itineraryFlightNumber = normalizeCode(item.flightNumber);
      const bookingAirlineCode = normalizeCode(booking.airlineCode);
      const bookingFlightNumber = normalizeCode(booking.flightNumber);
      const identifiersChanged =
        itineraryAirlineCode !== bookingAirlineCode ||
        itineraryFlightNumber !== bookingFlightNumber;

      const mergedDeparture = mergeItineraryTimeOntoDay(item.startDate, item.startTime ?? null);
      const mergedArrival = mergeItineraryTimeOntoDay(
        item.endDate ?? item.startDate,
        item.endTime ?? null,
      );

      const scheduleDrift = (() => {
        const tolMs = 60_000;
        const dep = booking.departureDate?.getTime() ?? NaN;
        const arr = booking.arrivalDate?.getTime() ?? NaN;
        if (Math.abs(mergedDeparture.getTime() - dep) >= tolMs) return true;
        if (Math.abs(mergedArrival.getTime() - arr) >= tolMs) return true;
        return false;
      })();

      const flightMetaChanged = identifiersChanged && (!!itineraryAirlineCode || !!itineraryFlightNumber);

      if (flightMetaChanged || scheduleDrift) {
        const provider =
          [itineraryAirlineCode, itineraryFlightNumber].filter(Boolean).join(' ') ||
          item.title ||
          booking.provider;
        await updateBooking(
          tripId,
          booking.id,
          {
            ...(flightMetaChanged
              ? {
                  provider,
                  rawEmailSubject: item.title,
                  location: item.location || item.address || booking.location,
                  airlineCode: itineraryAirlineCode,
                  flightNumber: itineraryFlightNumber,
                }
              : {}),
            departureDate: mergedDeparture,
            arrivalDate: mergedArrival,
            flightStatus: null,
            lastStatusCheck: null,
            checkInLink: null,
            gate: null,
            terminal: null,
            checkInAvailable: false,
          },
          { bypassOffline: true },
        );
      }
      await refreshFlightStatus(booking.id, tripId);
    } catch (err) {
      console.error('Failed to refresh flight status:', err);
      Alert.alert('Unable to refresh status', humanizeAsyncError(err, 'Please try again shortly.'));
    } finally {
      setRefreshingStatus(false);
    }
  }, [tripId, booking?.id, booking?.airlineCode, booking?.flightNumber, booking?.provider, booking?.location, item.airlineCode, item.flightNumber, item.title, item.location, item.address, item.startDate, item.endDate]);

  const handleOpenCheckIn = useCallback(async () => {
    if (!tripId || !booking?.id) return;
    setCheckInLoading(true);
    try {
      let checkInLink: string | null = null;
      try {
        checkInLink = await fetchCheckInLink(booking.id, tripId);
      } catch {
        checkInLink = null;
      }
      if (!checkInLink) {
        const code = booking.airlineCode ?? (booking.provider ? booking.provider.replace(/\d+/g, '').trim() : undefined);
        checkInLink = getFallbackCheckInUrl(code);
      }
      if (checkInLink) {
        await Linking.openURL(checkInLink);
      } else {
        Alert.alert('Error', 'Could not open check-in. Try visiting your airline’s website.');
      }
    } catch (err) {
      console.error('Failed to open check-in:', err);
      const fallback = getFallbackCheckInUrl(booking.airlineCode ?? (booking.provider ? booking.provider.replace(/\d+/g, '').trim() : undefined));
      if (fallback) {
        try {
          await Linking.openURL(fallback);
        } catch {
          Alert.alert('Error', 'Could not open check-in.');
        }
      } else {
        Alert.alert('Error', 'Could not open check-in.');
      }
    } finally {
      setCheckInLoading(false);
    }
  }, [tripId, booking?.id, booking?.airlineCode, booking?.provider]);

  const handleUpdatePreferences = useCallback(
    async (prefs: FlightNotificationPreferences) => {
      if (!tripId || !booking?.id) return;
      setUpdatingPrefs(true);
      try {
        await updateFlightNotificationPreferences(booking.id, tripId, prefs);
      } catch (err) {
        console.error('Failed to update notification preferences:', err);
        Alert.alert('Update failed', humanizeAsyncError(err, 'Could not update preferences. Please try again.'));
      } finally {
        setUpdatingPrefs(false);
      }
    },
    [tripId, booking?.id],
  );

  const when = useMemo(() => {
    const date = formatDate(item.startDate);
    const start = formatTime(item.startTime);
    const end = formatTime(item.endTime);
    if (!start) return date;
    return end ? `${date} • ${start}–${end}` : `${date} • ${start}`;
  }, [item.startDate, item.startTime, item.endTime]);

  const loc = formatItineraryLocationDisplay(item);

  const mapsUrl = useMemo(
    () => buildGoogleMapsSearchUrl(item),
    [item.address, item.location, item.coordinates?.latitude, item.coordinates?.longitude],
  );

  const attachments = useMemo(() => {
    // Avoid expensive work unless user expands.
    if (!showAttachments) return [];
    return normalizeAttachments(item.attachments, item.documents || []);
  }, [showAttachments, item.attachments, item.documents]);

  const itineraryDepartureAt = useMemo(
    () => itineraryCalendarHint(item.startDate, item.startTime),
    [item.startDate, item.startTime],
  );
  const itineraryArrivalAt = useMemo(
    () => itineraryCalendarHint(item.endDate ?? item.startDate, item.endTime),
    [item.endDate, item.startDate, item.endTime],
  );

  const attachmentCount = useMemo(() => {
    const urls = new Set<string>();
    (Array.isArray(item.attachments) ? item.attachments : []).forEach((att: any) => {
      const u = (att?.url || '').trim();
      if (u) urls.add(u);
    });
    (Array.isArray(item.documents) ? item.documents : []).forEach((u: any) => {
      const s = typeof u === 'string' ? u.trim() : '';
      if (s) urls.add(s);
    });
    return urls.size;
  }, [item.attachments, item.documents]);

  const openMap = async () => {
    if (!mapsUrl) return;
    try {
      await Linking.openURL(mapsUrl);
    } catch {
      Alert.alert('Error', 'Could not open maps');
    }
  };

  const handleMarkDone = useCallback(async () => {
    if (!tripId || !canMarkDone) return;
    setMarkingDone(true);
    try {
      await updateItineraryItem(tripId, item.id, { status: 'completed' });
      setMarkedDoneLocally(true);
      showSuccess?.(`Marked "${item.title}" as done`);
      setTimeout(() => {
        onMarkDoneSuccess?.();
      }, 450);
    } catch (err) {
      console.error('[ItineraryItemDetails] Failed to mark item done', err);
      showError?.('Could not mark this activity as done. Try again.');
    } finally {
      setMarkingDone(false);
    }
  }, [canMarkDone, item.id, item.title, onMarkDoneSuccess, showError, showSuccess, tripId]);

  const openAttachment = async (att: ItineraryAttachment) => {
    const url = (att?.url || '').trim();
    if (!url) {
      Alert.alert('Attachment unavailable', 'This attachment has no file link.');
      return;
    }
    const knownOfflinePath = att.offlinePath?.trim() || null;
    try {
      if (onViewDocument) {
        onViewDocument(url, {
          offlinePath: knownOfflinePath,
          fileName: att.name,
          contentType: att.contentType,
        });
        return;
      }
      let resolvedOfflinePath: string | null = knownOfflinePath;
      if (!resolvedOfflinePath) {
        try {
          resolvedOfflinePath = await Promise.race<string | null>([
            resolveItineraryAttachmentLocalUri(att),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 450)),
          ]);
        } catch {
          resolvedOfflinePath = null;
        }
      }
      const target = resolvedOfflinePath || url;
      await Linking.openURL(target);
    } catch {
      try {
        const target = knownOfflinePath || url;
        await Linking.openURL(target);
      } catch {
        Alert.alert('Error', 'Failed to open document');
      }
    }
  };

  return (
    <View style={{ gap: theme.spacing(1.5), opacity: isDone ? 0.72 : 1 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flexWrap: 'wrap' }}>
        <ThemedText
          variant="bodyStrong"
          style={{
            fontSize: 18,
            fontWeight: '800',
            textDecorationLine: item.status === 'completed' || markedDoneLocally ? 'line-through' : 'none',
            flexShrink: 1,
          }}
        >
          {item.title}
        </ThemedText>
        <View
          style={{
            paddingHorizontal: theme.spacing(1),
            paddingVertical: 3,
            borderRadius: theme.radii.pill,
            backgroundColor: theme.colors.primary + '12',
          }}
        >
          <Caption style={{ color: theme.colors.primary, fontWeight: '700' }}>{categoryLabel(item.category)}</Caption>
        </View>
        {(item.status === 'completed' || markedDoneLocally) && (
          <View
            style={{
              paddingHorizontal: theme.spacing(1),
              paddingVertical: 3,
              borderRadius: theme.radii.pill,
              backgroundColor: theme.colors.success + '18',
            }}
          >
            <Caption style={{ color: theme.colors.success, fontWeight: '700' }}>Done</Caption>
          </View>
        )}
      </View>

      <View style={{ gap: theme.spacing(0.75) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
          <Ionicons name="time-outline" size={16} color={theme.colors.textSecondary} />
          <Body color={theme.colors.textSecondary}>{when}</Body>
        </View>
        {!!loc && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
            <Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ flex: 1 }}>
              {loc}
            </Body>
          </View>
        )}
      </View>

      {!!item.description && (
        <Card variant="outlined" style={{ padding: theme.spacing(1.25) }}>
          <Body color={theme.colors.textSecondary} style={{ lineHeight: 20 }}>
            {item.description}
          </Body>
        </Card>
      )}

      {!!mapsUrl && (
        <Button
          label="View on map"
          variant="secondary"
          onPress={openMap}
          icon={<Ionicons name="map-outline" size={16} color={theme.colors.primary} />}
        />
      )}

      {trip && onOpenMobility ? (
        <GettingAroundRow
          trip={trip}
          item={item}
          isTripActive={Boolean(isTripActive)}
          onOpenMobility={onOpenMobility}
        />
      ) : null}

      {canMarkDone && (
        <Button
          label={markingDone ? 'Marking done…' : 'Mark done'}
          variant="primary"
          disabled={markingDone}
          onPress={() => void handleMarkDone()}
          icon={<Ionicons name="checkmark-circle-outline" size={18} color={theme.colors.surface} />}
          accessibilityLabel="Mark this activity as done"
        />
      )}

      {/* Flight Status Card - visible when tapping a flight with linked booking */}
      {item.category === 'flight' && booking && tripId && (
        <FlightStatusCard
          booking={booking}
          itineraryDepartureAt={itineraryDepartureAt}
          itineraryArrivalAt={itineraryArrivalAt}
          onRefresh={handleRefreshFlightStatus}
          isRefreshing={refreshingStatus}
          onCheckIn={handleOpenCheckIn}
          isCheckInLoading={checkInLoading}
          onUpdatePreferences={handleUpdatePreferences}
          isUpdatingPreferences={updatingPrefs}
        />
      )}

      {/* Attachments (lazy) */}
      {attachmentCount > 0 && (
        <Card variant="outlined" style={{ padding: theme.spacing(1.25) }}>
          <Pressable
            onPress={() => setShowAttachments((v) => !v)}
            style={({ pressed }) => ({
              opacity: pressed ? 0.7 : 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            })}
            accessibilityRole="button"
            accessibilityLabel={showAttachments ? 'Hide attachments' : 'Show attachments'}
            accessibilityState={{ expanded: showAttachments }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flex: 1 }}>
              <Ionicons name="document-text-outline" size={18} color={theme.colors.textSecondary} />
              <View style={{ flex: 1 }}>
                <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                  View attachments ({attachmentCount})
                </ThemedText>
                {!showAttachments && (
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                    Tap to open attached files
                  </Caption>
                )}
              </View>
            </View>
            <Ionicons name={showAttachments ? 'chevron-up' : 'chevron-down'} size={18} color={theme.colors.textSecondary} />
          </Pressable>

          {showAttachments && (
            <View style={{ marginTop: theme.spacing(1), gap: theme.spacing(0.75) }}>
              {attachments.map((att) => (
                <Pressable
                  key={att.id}
                  onPress={() => void openAttachment(att)}
                  style={({ pressed }) => ({
                    opacity: pressed ? 0.7 : 1,
                    paddingVertical: theme.spacing(0.75),
                    paddingHorizontal: theme.spacing(1),
                    borderRadius: theme.radii.md,
                    backgroundColor: theme.colors.borderMuted + '20',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: theme.spacing(1),
                  })}
                >
                  <Body style={{ flex: 1 }}>{att.name || 'Attachment'}</Body>
                  <Ionicons name="open-outline" size={16} color={theme.colors.primary} />
                </Pressable>
              ))}
            </View>
          )}
        </Card>
      )}

      {canEdit && (onEditItem || onDeleteItem) && (
        <View style={{ gap: theme.spacing(1) }}>
          {onEditItem && (
            <Button
              label={attachmentCount > 0 ? 'Manage attachments' : 'Add attachment'}
              variant="secondary"
              onPress={() => onEditItem(item)}
              icon={<Ionicons name="attach-outline" size={16} color={theme.colors.primary} />}
            />
          )}
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
          {onEditItem && (
            <Button
              label="Edit"
              variant="secondary"
              onPress={() => onEditItem(item)}
              icon={<Ionicons name="create-outline" size={16} color={theme.colors.primary} />}
              style={{ flex: 1 }}
            />
          )}
          {onDeleteItem && (
            <Button
              label="Delete"
              variant="danger"
              onPress={() => onDeleteItem(item)}
              icon={<Ionicons name="trash-outline" size={16} color={theme.colors.surface} />}
              style={{ flex: 1 }}
            />
          )}
          </View>
        </View>
      )}

      {/* Notes: move below primary actions so it doesn't push key actions off-screen */}
      <ItemNotesSection item={item} canEdit={canEdit} />
    </View>
  );
}


